import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const LAST_UPDATED = "26 June 2026";

function Section({ n, title, children }) {
  return (
    <section className="mt-10 scroll-mt-24" id={`p-${n}`} data-testid={`privacy-section-${n}`}>
      <h2 className="font-display text-2xl tracking-tight">
        <span className="text-neutral-400 mr-2">{n}.</span>{title}
      </h2>
      <div className="mt-3 space-y-3 text-sm text-neutral-700 leading-relaxed">{children}</div>
    </section>
  );
}

const SECTIONS = [
  "Introduction & scope", "Who we are", "Information we collect", "How we use your information",
  "Legal bases for processing", "How we share information", "Cookies & analytics",
  "Data retention", "Your privacy rights", "Security", "International transfers",
  "Children's privacy", "Changes to this policy", "Contact us",
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-8">
        <div className="label-eyebrow">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Privacy Policy.</h1>
        <p className="mt-4 text-sm text-neutral-500 max-w-2xl">Last updated {LAST_UPDATED}</p>
        <p className="mt-4 text-base text-neutral-600 max-w-3xl leading-relaxed">
          This Privacy Policy explains how Your Classic Style collects, uses, shares and protects your personal
          information when you use our marketplace. It works alongside our{" "}
          <Link to="/terms" className="underline">Terms &amp; Conditions</Link>.
        </p>
      </div>

      <div className="mt-8 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="h-fit lg:sticky lg:top-24 hairline p-5" data-testid="privacy-toc">
          <div className="label-eyebrow">Contents</div>
          <ol className="mt-3 space-y-1.5 text-xs text-neutral-600">
            {SECTIONS.map((t, i) => (
              <li key={t}><a href={`#p-${i + 1}`} className="hover:text-black transition-colors">{i + 1}. {t}</a></li>
            ))}
          </ol>
        </aside>

        <div>
          <div className="hairline p-5 flex items-start gap-3" style={{ borderColor: "#2563EB" }} data-testid="privacy-disclaimer">
            <ShieldCheck size={16} style={{ color: "#2563EB" }} className="mt-0.5 shrink-0" />
            <p className="text-xs text-neutral-600 leading-relaxed">
              This document is a plain-English template for your convenience and is not legal advice. Before operating
              commercially, have a qualified privacy lawyer adapt it to your business and to the laws that apply to you
              (for example GDPR, UK GDPR, CCPA/CPRA), and confirm your lawful bases, retention periods and data-transfer
              mechanisms.
            </p>
          </div>

          <Section n={1} title="Introduction & scope">
            <p>
              This policy applies to personal information we process about visitors, buyers and sellers through the Your
              Classic Style website and related services (the "Platform"). By using the Platform you acknowledge the
              practices described here.
            </p>
          </Section>

          <Section n={2} title="Who we are">
            <p>
              Your Classic Style (the "Platform", "we", "us"), operating from the State of Georgia, United States, is the
              operator of this marketplace and the controller of the personal information described below.
              <em> (Your lawyer may add your registered business address and, if you serve EU/UK users, a data-protection
              representative before going live.)</em>
            </p>
          </Section>

          <Section n={3} title="Information we collect">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Account data</strong> — name, email and password (stored only as a secure hash).</li>
              <li><strong>Listing & content data</strong> — photos, descriptions, measurements, condition grades and disclosures you upload.</li>
              <li><strong>Transaction data</strong> — orders, offers, delivery selections, and the limited details needed to fulfil a purchase. Card details are handled by our payment partner; we do not store full card numbers.</li>
              <li><strong>Communications</strong> — messages, reviews, claims and support requests.</li>
              <li><strong>Technical & usage data</strong> — device, browser, IP address, pages viewed and actions taken, collected via cookies and similar technologies.</li>
            </ul>
          </Section>

          <Section n={4} title="How we use your information">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>To create and manage your account and listings;</li>
              <li>To process orders, offers, payments, payouts, refunds and claims;</li>
              <li>To operate Buyer Protection, authenticity screening and fraud/safety checks;</li>
              <li>To provide customer support and send service messages, brand alerts and, where permitted, marketing;</li>
              <li>To improve, secure and analyse the Platform, and to comply with legal obligations.</li>
            </ul>
          </Section>

          <Section n={5} title="Legal bases for processing">
            <p>
              Where GDPR or similar laws apply, we rely on: performance of our contract with you (to run your account and
              transactions); our legitimate interests (to secure, improve and market the Platform); your consent (for
              certain cookies and marketing, which you may withdraw); and compliance with legal obligations.
            </p>
          </Section>

          <Section n={6} title="How we share information">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Between buyers and sellers</strong> — the limited details needed to complete and ship an order.</li>
              <li><strong>Payment processing</strong> — with our payment partner (Stripe) to take payment and make payouts.</li>
              <li><strong>Shipping & carriers</strong> — delivery details, including any pickup-point selection.</li>
              <li><strong>Service providers</strong> — hosting, storage, analytics and communications vendors acting on our instructions.</li>
              <li><strong>Legal & safety</strong> — with authorities or rights holders where required by law or to prevent fraud, counterfeiting or harm.</li>
            </ul>
            <p>We do not sell your personal information.</p>
          </Section>

          <Section n={7} title="Cookies & analytics">
            <p>
              We use cookies and similar technologies to keep you signed in, remember preferences and understand how the
              Platform is used. You can control non-essential cookies through your browser settings or any cookie banner
              we provide. <em>(List your specific analytics providers before going live.)</em>
            </p>
          </Section>

          <Section n={8} title="Data retention">
            <p>
              We keep personal information for as long as your account is active and as needed to provide the Platform,
              then for the periods required to meet legal, tax, accounting and dispute-resolution obligations, after
              which it is deleted or anonymised. <em>(Set your specific retention periods before going live.)</em>
            </p>
          </Section>

          <Section n={9} title="Your privacy rights">
            <p>
              Depending on where you live, you may have rights to access, correct, delete, restrict or object to the
              processing of your personal information, to data portability, and to withdraw consent. California residents
              have rights under the CCPA/CPRA, including to know, delete, correct and opt out of "sharing"/"selling"
              (which we do not do). To exercise any right, contact us using Section 14; we will respond within the time
              the law requires and will not discriminate against you for exercising your rights.
            </p>
          </Section>

          <Section n={10} title="Security">
            <p>
              We use administrative, technical and organisational measures to protect your information, including
              hashed passwords and encrypted connections. No system is perfectly secure, so we cannot guarantee absolute
              security; please use a strong, unique password and keep your credentials confidential.
            </p>
          </Section>

          <Section n={11} title="International transfers">
            <p>
              Your information may be processed in countries other than your own. Where required, we use appropriate
              safeguards (such as standard contractual clauses) for such transfers. <em>(Confirm your transfer
              mechanisms before going live.)</em>
            </p>
          </Section>

          <Section n={12} title="Children's privacy">
            <p>
              The Platform is not directed to children under 18, and we do not knowingly collect their personal
              information. If you believe a child has provided us information, contact us and we will delete it.
            </p>
          </Section>

          <Section n={13} title="Changes to this policy">
            <p>
              We may update this policy from time to time. We will post the revised version here with a new "last
              updated" date and, where appropriate, notify you. Continued use after changes take effect means you accept
              the updated policy.
            </p>
          </Section>

          <Section n={14} title="Contact us">
            <p>
              Questions or requests about your privacy? Contact us at{" "}
              <span className="underline" data-testid="privacy-email">kishawhite@yourclassicstyle.onmicrosoft.com</span>.
            </p>
          </Section>

          <p className="mt-12 text-xs text-neutral-400">
            © {new Date().getFullYear()} Your Classic Style. See also our{" "}
            <Link to="/terms" className="underline">Terms &amp; Conditions</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
